/**
 * Left-edge drag resize for the docked artifact panel: the shared gesture (use-edge-resize.ts) bound to the chat
 * area's width. The container is measured once per gesture, at pointer-down, and the stored width is clamped
 * against it there too — between gestures the CSS `clamp(10cqi, {width}px, 75cqi)` keeps the painted width honest
 * for free, so nothing observes the container and the stored width is never ratcheted down by a transient layout.
 * The bounds themselves are React-free and live in `geometry/panel-metrics.ts`.
 */

import { useCallback } from "react"
import { clampPanelWidth, dockedPanelWidthCss } from "../geometry/panel-metrics"
import { useEdgeResize } from "./use-edge-resize"
import type { ResizeHandleProps, UseEdgeResizeResult, WidthClamp } from "./use-edge-resize"

export type { ResizeHandleProps }

export interface UsePanelResizeResult {
  panelRef: UseEdgeResizeResult["elementRef"]
  resizeHandleProps: ResizeHandleProps
}

/** @param containerRef the chat area the panel docks into; its width is the clamp basis for a drag. */
export function usePanelResize(
  width: number,
  onWidthChange: (width: number) => void,
  containerRef: { readonly current: HTMLElement | null },
): UsePanelResizeResult {
  const captureClamp = useCallback((): WidthClamp | null => {
    const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 0
    if (containerWidth <= 0) return null
    return (candidate: number) => clampPanelWidth(candidate, containerWidth)
  }, [containerRef])

  // settleDrag is deliberately not surfaced: nothing closes this panel mid-gesture the way the dock does.
  // `dockedPanelWidthCss` is the same string artifact-panel.tsx gives React, so the two cannot drift.
  const { elementRef, resizeHandleProps } = useEdgeResize({ width, onWidthChange, captureClamp, widthStyle: dockedPanelWidthCss })
  return { panelRef: elementRef, resizeHandleProps }
}
